import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { dialogs } from "../../context/DialogContext";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";
import { parseBusquedaContacto } from "../../utils/parseBusquedaContacto";
import { provinciaNombreDesdeValor } from "../../utils/provinciasEs";
import usePermiso from "../../hooks/usePermiso";
import { useCaptacionRelacionContacto } from "../../hooks/useCaptacionRelacionContacto";
import ModalRelacionContactoExistente from "../ModalRelacionContactoExistente";
import CamposDomicilioContacto from "../contactos/CamposDomicilioContacto";
import {
  domicilioContactoVacio,
  hidratarDomicilioContacto,
  rellenarDomicilioSiFalta,
  payloadDomicilioContacto,
  fichaDomicilioDesdeForm,
} from "../../utils/domicilioContacto";
import { useContactoSoftWarnings } from "../../hooks/useContactoSoftWarnings";
import { debeRevisarDocumentoEnInput } from "../../utils/contactoSoftValidation";

const PERM_ID_POR_CAMPO = {
  nombre: "nombre_propietario",
  telefono: "telefono_propietario",
  dni_nie: "dni_propietario",
  email: "email_propietario",
};

const CAMPOS = [
  { key: "nombre", label: "Nombre *", full: false },
  { key: "apellidos", label: "Apellidos", full: false },
  { key: "telefono", label: "Teléfono", full: false },
  { key: "email", label: "Email", full: false },
  { key: "dni_nie", label: "DNI/NIE", full: false },
];

const FORM_VACIO = {
  nombre: "",
  apellidos: "",
  telefono: "",
  email: "",
  dni_nie: "",
  ...domicilioContactoVacio(),
  porcentaje: 100,
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isContactoUuid = (v) => UUID_RE.test(String(v || ""));

export default function ModalCrearPropietario({
  open,
  onClose,
  onCreado,
  onActualizado,
  propietarioEditar = null,
  porcentajeInicial = 100,
  textoBusqueda = "",
  placesLocalityHint = null,
}) {
  const esEdicion = Boolean(propietarioEditar);
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const relacionContacto = useCaptacionRelacionContacto({
    excludeContactoId: propietarioEditar?.contacto_id || propietarioEditar?.id || null,
    origen: "crm_propietario",
    modo: "propietario",
  });

  useEffect(() => {
    if (!open) {
      relacionContacto.resetSeen();
      relacionContacto.closeQuiet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [form, setForm] = useState({ ...FORM_VACIO, porcentaje: porcentajeInicial });
  const [guardando, setGuardando] = useState(false);
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const [placesContext, setPlacesContext] = useState(null);
  // Alcance "Propio": si este propietario no está ligado a ninguna propiedad/
  // captación del agente, el backend deniega el PATCH; aquí desactivamos el
  // formulario para no mostrar un 403 al guardar.
  const [puedeModificarRegistro, setPuedeModificarRegistro] = useState(true);
  const softWarn = useContactoSoftWarnings(`propietario-${propietarioEditar?.contacto_id || propietarioEditar?.id || 'nuevo'}`);
  const ctxContacto = {
    idioma: 'es',
    pais: form.pais || 'ES',
    dni_nie: form.dni_nie,
    dni: form.dni_nie,
  };
  const revisarSoft = (tipo, valor, key) => {
    softWarn.checkField(tipo, valor, ctxContacto).then((v) => {
      if (v !== valor) setForm((f) => ({ ...f, [key]: v }));
    });
  };

  useEffect(() => {
    if (!open) return;
    setGuardando(false);
    setPlacesContext(null);
    if (!propietarioEditar) {
      const parsed = parseBusquedaContacto(textoBusqueda);
      setForm({
        ...FORM_VACIO,
        porcentaje: porcentajeInicial,
        nombre: parsed.nombre || "",
        apellidos: parsed.apellidos || "",
        telefono: parsed.telefono || "",
        email: parsed.email || "",
      });
      return;
    }
    const base = {
      nombre: propietarioEditar.nombre || "",
      apellidos: propietarioEditar.apellidos || "",
      telefono: propietarioEditar.telefono || "",
      email: propietarioEditar.email || "",
      // Ampliar: en captación (contacto_id "titular"/sin UUID) DNI y domicilio
      // viven en propietarios_ficha / datos_extras, no aún en `contactos`.
      dni_nie: propietarioEditar.dni_nie || propietarioEditar.dni || "",
      ...hidratarDomicilioContacto(propietarioEditar),
      provincia: provinciaNombreDesdeValor(
        propietarioEditar.provincia
        || propietarioEditar.domicilio_provincia
        || hidratarDomicilioContacto(propietarioEditar).provincia
        || "",
      ),
      porcentaje: propietarioEditar.porcentaje ?? porcentajeInicial,
    };
    setForm(base);
    setPuedeModificarRegistro(true);
    if (!isContactoUuid(propietarioEditar.contacto_id)) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/contactos/${propietarioEditar.contacto_id}`);
        const c = data?.data;
        if (!alive || !c) return;
        // Contacto CRM: rellena huecos; no pisa DNI/dirección ya traídos de la ficha
        setForm((f) => ({
          ...f,
          nombre: c.nombre || f.nombre,
          apellidos: c.apellidos || f.apellidos || "",
          telefono: c.telefono || f.telefono || "",
          email: c.email || f.email || "",
          dni_nie: c.dni_nie || f.dni_nie || "",
          ...rellenarDomicilioSiFalta(f, c),
          provincia: provinciaNombreDesdeValor(
            rellenarDomicilioSiFalta(f, c).provincia || "",
          ),
        }));
        if (c.puedeModificar === false) setPuedeModificarRegistro(false);
      } catch {
        /* datos de la fila */
      }
    })();
    return () => { alive = false; };
  }, [open, propietarioEditar, porcentajeInicial, textoBusqueda]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const pobHint = (placesLocalityHint?.poblacion || "").trim();
    const provHint = (placesLocalityHint?.provincia || "").trim();
    const pob = (form.poblacion || "").trim() || pobHint;
    const prov = (form.provincia || "").trim() || provHint;
    if (!pob) {
      setPlacesContext(null);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/geo/localidad-centro", {
          params: { poblacion: pob, provincia: prov, pais: "España" },
        });
        if (!alive) return;
        if (data?.ok && data.lat != null && data.lng != null) {
          setPlacesContext({ latitude: data.lat, longitude: data.lng });
        } else setPlacesContext(null);
      } catch {
        if (alive) setPlacesContext(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, form.poblacion, form.provincia, placesLocalityHint?.poblacion, placesLocalityHint?.provincia]);

  if (!open) return null;

  const guardar = async () => {
    if (!form.nombre?.trim()) {
      await dialogs.alert("El nombre es obligatorio");
      return;
    }
    const pct = Number(form.porcentaje);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      await dialogs.alert("El porcentaje debe estar entre 0 y 100");
      return;
    }
    setGuardando(true);
    const payload = {
      nombre: form.nombre.trim(),
      apellidos: form.apellidos?.trim() || null,
      telefono: form.telefono?.trim() || null,
      email: form.email?.trim() || null,
      dni_nie: form.dni_nie?.trim() || null,
      ...payloadDomicilioContacto(form),
    };
    const fichaDom = fichaDomicilioDesdeForm(form);
    try {
      if (esEdicion) {
        let nombre = payload.nombre;
        let apellidos = payload.apellidos || "";
        let telefono = payload.telefono || "";
        let email = payload.email || "";
        if (isContactoUuid(propietarioEditar.contacto_id)) {
          const { data } = await api.patch(`/contactos/${propietarioEditar.contacto_id}`, {
            ...payload,
            tipo: "propietario",
          });
          const c = data?.data;
          if (c) {
            nombre = c.nombre;
            apellidos = c.apellidos || "";
            telefono = c.telefono || "";
            email = c.email || "";
          }
        }
        if (onActualizado) {
          const keepOpen = (await onActualizado({
            ...propietarioEditar,
            nombre,
            apellidos,
            telefono,
            email,
            dni_nie: payload.dni_nie || "",
            ...fichaDom,
            porcentaje: pct,
          })) === false;
          if (keepOpen) return;
        }
        onClose();
        return;
      }
      const { data } = await api.post("/contactos", {
        ...payload,
        tipo: "propietario",
      });
      const c = data?.data;
      if (!c?.id) throw new Error("Sin datos de contacto");
      if (onCreado) {
        const keepOpen = (await onCreado({
          id: crypto.randomUUID(),
          contacto_id: c.id,
          nombre: c.nombre,
          apellidos: c.apellidos || "",
          telefono: c.telefono || "",
          email: c.email || "",
          dni_nie: c.dni_nie || payload.dni_nie || "",
          ...fichaDom,
          porcentaje: pct,
        })) === false;
        if (keepOpen) return;
      }
      onClose();
    } catch (e) {
      const st = e?.response?.status;
      const msg =
        st === 502 || st === 503 || st === 504
          ? "El servidor no respondió a tiempo (error " +
            st +
            "). Comprueba si los datos se guardaron recargando la página antes de volver a intentarlo."
          : e?.response?.data?.error || e?.message ||
            (esEdicion ? "No se pudo actualizar el propietario" : "No se pudo crear el propietario");
      await dialogs.alert(msg);
    } finally {
      setGuardando(false);
    }
  };

  const inpSt = {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 13,
    background: isDark ? "#111827" : "#f9fafb",
    border: `1.5px solid ${isDark ? "#374151" : "#e5e7eb"}`,
    color: isDark ? "#f9fafb" : "#111827",
    outline: "none",
    fontFamily: "inherit",
  };

  const labelSt = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: t.textMuted,
    display: "block",
    marginBottom: 5,
  };

  const modal = (
    <div
      data-modal-form
      onKeyDown={(e) => {
        if (e.key === "Enter") e.preventDefault();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Por encima del overlay "Ver ficha completa" del Pipeline (zIndex 1080):
        // si no, este modal se abre pero queda tapado y parece que no responde.
        zIndex: 2000,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        ref={dragRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 608,
          maxWidth: "94vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          background: t.bgCard,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          border: `1px solid ${t.border}`,
          position: "relative",
          ...panelPositionStyle,
        }}
      >
        <div
          style={{
            height: 3,
            flexShrink: 0,
            background: "linear-gradient(90deg, #60a5fa, #2563eb, #1d4ed8, #2563eb, #60a5fa)",
          }}
        />
        <div
          onMouseDown={startDrag}
          style={{
            padding: "20px 24px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
              }}
            >
              🏠
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: t.textPrimary }}>
                {esEdicion ? "Editar propietario" : "Nuevo propietario"}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: t.textMuted }}>
                {esEdicion
                  ? "Actualiza los datos del propietario en contactos"
                  : "Se creará en contactos y se vinculará a esta propiedad"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "#1f2937" : "#f3f4f6",
              cursor: "pointer",
              fontSize: 16,
              color: t.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${t.border}, transparent)` }} />
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 8px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {CAMPOS.map(({ key, label, full }) => {
              const permId = PERM_ID_POR_CAMPO[key];
              if (permId && !puede(permId, "ver")) return null;
              const disabledPerm = (permId ? !puede(permId, "modificar") : false) || !puedeModificarRegistro;
              return (
              <div key={key} style={{ gridColumn: full ? "span 2" : "span 1" }}>
                <label style={labelSt}>{label}</label>
                <input
                  value={form[key]}
                  onChange={(e) => {
                    const valor = e.target.value;
                    setForm((f) => ({ ...f, [key]: valor }));
                    if (key === 'dni_nie' && debeRevisarDocumentoEnInput(valor)) {
                      revisarSoft('dni_nie', valor, 'dni_nie');
                    }
                    if (key === "telefono" || key === "email") {
                      const tel = key === "telefono" ? valor : form.telefono;
                      const em = key === "email" ? valor : form.email;
                      relacionContacto.check(tel, em);
                    }
                  }}
                  onBlur={() => {
                    const valor = form[key] || '';
                    if (key === 'apellidos') revisarSoft('apellidos', valor, 'apellidos');
                    else if (key === 'dni_nie') revisarSoft('dni_nie', valor, 'dni_nie');
                    else if (key === 'telefono') revisarSoft('telefono', valor, 'telefono');
                    else if (key === 'email') revisarSoft('email', valor, 'email');
                  }}
                  disabled={disabledPerm}
                  style={{ ...inpSt, opacity: disabledPerm ? 0.6 : 1, cursor: disabledPerm ? "not-allowed" : "text" }}
                  type={key === "email" ? "email" : key === "telefono" ? "tel" : "text"}
                />
              </div>
              );
            })}
            <CamposDomicilioContacto
              form={form}
              setForm={setForm}
              inpSt={inpSt}
              labelSt={labelSt}
              isDark={isDark}
              disabled={!puedeModificarRegistro}
              placesLocalityContext={placesContext}
            />
            <div style={{ gridColumn: "span 2" }}>
              <label style={labelSt}>% de propiedad</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.porcentaje}
                onChange={(e) => setForm((f) => ({ ...f, porcentaje: e.target.value }))}
                style={inpSt}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            padding: "16px 24px 20px",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            flexShrink: 0,
            borderTop: `1px solid ${t.border}`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            style={{
              padding: "11px 24px",
              borderRadius: 10,
              border: `2px solid ${t.border}`,
              background: isDark ? "#1f2937" : "#f3f4f6",
              color: t.textMuted,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando || (esEdicion && !puedeModificarRegistro)}
            title={esEdicion && !puedeModificarRegistro ? "No puedes modificar este propietario: no está ligado a ninguna propiedad o captación tuya" : undefined}
            style={{
              padding: "11px 30px",
              borderRadius: 10,
              border: "none",
              background: esEdicion && !puedeModificarRegistro ? (isDark ? "#374151" : "#d1d5db") : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              color: "#fff",
              cursor: guardando ? "wait" : (esEdicion && !puedeModificarRegistro ? "not-allowed" : "pointer"),
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 700,
              boxShadow: esEdicion && !puedeModificarRegistro ? "none" : "0 4px 14px rgba(37,99,235,0.35)",
              opacity: guardando ? 0.7 : 1,
            }}
          >
            {guardando ? (esEdicion ? "Guardando…" : "Creando…") : (esEdicion ? "Guardar cambios" : "Crear y añadir")}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {modal}
      {softWarn.modal}
      <ModalRelacionContactoExistente
        open={relacionContacto.open}
        matches={relacionContacto.matches}
        canal={relacionContacto.canal}
        contexto="contacto"
        onClose={relacionContacto.dismiss}
        onDescartar={() => {
          relacionContacto.dismissDiscard();
          onClose?.();
        }}
      />
    </>,
    document.body,
  );
}
